/**
 * 司机下拉选项。
 *
 * `account.name` 允许重名（见 schema.sql `idx_account_name_status` 注释），
 * 因此下拉的 value 必须用 accountId：
 * - 用姓名当 value 会让 rc-select 的虚拟列表出现重复 itemKey，滚动时重复渲染并丢项
 * - 用姓名反查 id 时同名只会命中一个账号，筛选结果会静默漏数据
 */
export interface DriverFilterOption {
    accountId: number;
    name: string;
    /** 仅重名项有值，作为原生 hover tooltip 区分同名司机 */
    title?: string;
}

/** 「未分配司机」哨兵，与真实 accountId（正整数自增）不会冲突 */
export const UNASSIGNED_DRIVER_ID = -1;

/**
 * 接口返回的司机 id 归一为 number。
 *
 * 后端 `Long` 标量是 `Coercing<Long, String>`（api-server `GraphQLScalarConfiguration.kt`），
 * **序列化为字符串**，而 TS 侧把它声明成 `id: number`（假类型，tsc 查不出来）。
 * 不归一会同时踩两个坑：`===` 去重失配导致同一司机出现两条选项、
 * `Map<number, string>` 用字符串 key 写入后按 number 取不到导致姓名回显成 `#<id>`。
 */
const toAccountId = (id: number | string): number => Number(id);

/** 把接口返回的司机姓名累积进缓存，供已选项在换关键词后仍能回显姓名 */
export const rememberDriverNames = (
    searched: Array<{ id: number | string; name: string }> | undefined,
    nameById: Map<number, string>
): void => {
    (searched ?? []).forEach((option) => nameById.set(toAccountId(option.id), option.name));
};

/** 选项集合内同名出现多次时，挂上带 accountId 的 tooltip；唯一姓名不设 title，保持 rc-select 默认行为 */
const withDuplicateNameTitle = (options: DriverFilterOption[]): DriverFilterOption[] => {
    const nameCount = new Map<string, number>();
    options.forEach((option) => nameCount.set(option.name, (nameCount.get(option.name) ?? 0) + 1));
    return options.map((option) => (
        (nameCount.get(option.name) ?? 0) > 1
            ? { ...option, title: `${option.name} #${option.accountId}` }
            : option
    ));
};

/**
 * 搜索结果并入「已选项」，否则 antd Select 无法回显已选标签。
 * 已选项未必在按关键词搜索的结果里，姓名从累积缓存取，取不到时退化显示 `#<id>`。
 */
export const buildDriverOptions = (
    searched: Array<{ id: number | string; name: string }> | undefined,
    selectedValues: string[] | undefined,
    nameById: Map<number, string>
): DriverFilterOption[] => {
    const merged: DriverFilterOption[] = (searched ?? []).map((option) => ({
        accountId: toAccountId(option.id),
        name: option.name
    }));
    (selectedValues ?? []).forEach((value) => {
        const accountId = Number(value);
        if (!Number.isFinite(accountId)) return;
        if (merged.some((option) => option.accountId === accountId)) return;
        merged.push({
            accountId,
            name: nameById.get(accountId) ?? (accountId === UNASSIGNED_DRIVER_ID ? '-' : `#${accountId}`)
        });
    });
    return withDuplicateNameTitle(merged);
};

/**
 * 下拉 value 转查询参数；哨兵与非法值由调用方另行处理，这里只取真实 accountId。
 * 无有效值时返回 undefined —— 空数组会被后端当成「driverIds in ()」而查不到任何数据。
 */
export const toDriverIds = (selectedValues: string[] | undefined): number[] | undefined => {
    const ids = (selectedValues ?? [])
        .map(Number)
        .filter((id) => Number.isFinite(id) && id > 0);
    return ids.length > 0 ? ids : undefined;
};

/** 选中项是否只有「未分配」——此时不传 driverIds，改传 driverIdNullOnly */
export const isUnassignedDriverOnly = (selectedValues: string[] | undefined): boolean =>
    selectedValues?.length === 1 && Number(selectedValues[0]) === UNASSIGNED_DRIVER_ID;
